import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { policiesApi } from "@/api/endpoints";
import type { RenewalBucket, RenewalItem, RenewalWorkbenchResponse } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatCurrency, formatDate } from "@/utils/format";

function urgencyClass(days: number): string {
  if (days <= 30) return "renewal-urgency-critical";
  if (days <= 60) return "renewal-urgency-warning";
  return "renewal-urgency-normal";
}

function recommendationClass(tag: string | null | undefined): string {
  if (tag === "at_risk") return "renewal-rec-at-risk";
  if (tag === "remarket") return "renewal-rec-remarket";
  return "renewal-rec-ok";
}

function RenewalRow({
  item,
  selected,
  onToggle,
  busy,
  onTask,
}: {
  item: RenewalItem;
  selected: boolean;
  onToggle: (policyId: string) => void;
  busy: string | null;
  onTask: (item: RenewalItem) => void;
}) {
  const rowBusy = busy === item.policy_id;

  return (
    <tr className={selected ? "renewal-row-selected" : undefined}>
      <td>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggle(item.policy_id)}
          aria-label={`Select ${item.policy_number}`}
        />
      </td>
      <td>
        <Link to={`/policies/${item.policy_id}`}>
          <strong>{item.policy_number}</strong>
        </Link>
        {item.recommendation_label ? (
          <span className={`renewal-rec-badge ${recommendationClass(item.recommendation_tag)}`}>
            {item.recommendation_label}
          </span>
        ) : null}
        {item.remarketing_recommended ? (
          <span className="renewal-remarket-badge">Remarket</span>
        ) : null}
      </td>
      <td>
        {item.customer_id ? (
          <Link to={`/clients/${item.customer_id}`}>{item.customer_name}</Link>
        ) : (
          item.customer_name ?? "—"
        )}
      </td>
      <td>{item.product_line ?? "—"}</td>
      <td>
        <span className={`renewal-days ${urgencyClass(item.days_until_renewal)}`}>
          {item.days_until_renewal}d
        </span>
        <span className="renewal-expiry"> · {formatDate(item.expiration_date)}</span>
      </td>
      <td>
        {item.days_since_contact != null ? `${item.days_since_contact}d` : "—"}
        {item.open_claims_count ? (
          <span className="renewal-claim-flag"> · {item.open_claims_count} claim(s)</span>
        ) : null}
      </td>
      <td>{item.premium_cents != null ? formatCurrency(item.premium_cents) : "—"}</td>
      <td>
        {item.premium_change_percent != null ? (
          <span className={item.premium_change_percent >= 10 ? "renewal-yoy-high" : "renewal-yoy-normal"}>
            {item.premium_change_percent >= 0 ? "+" : ""}
            {item.premium_change_percent}%
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="renewal-actions-cell">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={rowBusy}
          onClick={() => onTask(item)}
        >
          {rowBusy ? "…" : "Task"}
        </button>
        {item.has_open_task ? <span className="renewal-claim-flag">Task exists</span> : null}
        {item.customer_id ? (
          <Link to={`/rate-shop?customerId=${item.customer_id}`} className="btn btn-secondary">
            Rate shop
          </Link>
        ) : null}
      </td>
    </tr>
  );
}

function BucketPanel({
  bucket,
  selected,
  onToggle,
  busy,
  onTask,
}: {
  bucket: RenewalBucket;
  selected: Set<string>;
  onToggle: (policyId: string) => void;
  busy: string | null;
  onTask: (item: RenewalItem) => void;
}) {
  if (bucket.items.length === 0) {
    return <p className="empty-state">Nothing in this window.</p>;
  }

  return (
    <table className="data-table renewal-table">
      <thead>
        <tr>
          <th></th>
          <th>Policy</th>
          <th>Client</th>
          <th>Line</th>
          <th>Expires</th>
          <th>Last contact</th>
          <th>Premium</th>
          <th>YoY</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {bucket.items.map((item) => (
          <RenewalRow
            key={item.policy_id}
            item={item}
            selected={selected.has(item.policy_id)}
            onToggle={onToggle}
            busy={busy}
            onTask={onTask}
          />
        ))}
      </tbody>
    </table>
  );
}

export function RenewalsPage() {
  const [data, setData] = useState<RenewalWorkbenchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeBucket, setActiveBucket] = useState("0_30");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyPolicyId, setBusyPolicyId] = useState<string | null>(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [outreachModal, setOutreachModal] = useState<{
    title: string;
    items: Array<{ policy_number: string; customer_name: string | null; content: string }>;
    hint: string | null;
  } | null>(null);

  function reload() {
    return policiesApi
      .renewalsWorkbench(true)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load renewals"));
  }

  useEffect(() => {
    reload();
  }, []);

  const currentBucket = useMemo(
    () => data?.buckets.find((b) => b.bucket === activeBucket) ?? data?.buckets[0],
    [data, activeBucket],
  );

  function toggleSelect(policyId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(policyId)) next.delete(policyId);
      else next.add(policyId);
      return next;
    });
  }

  function selectBucket(bucket: RenewalBucket) {
    setSelected(new Set(bucket.items.map((item) => item.policy_id)));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function createSingleTask(item: RenewalItem) {
    setBusyPolicyId(item.policy_id);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await policiesApi.batchRenewalTasks([item.policy_id], "auto");
      await reload();
      setStatusMessage(`Task update complete: ${response.created} created · ${response.skipped} skipped.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create task");
    } finally {
      setBusyPolicyId(null);
    }
  }

  async function batchCreateTasks(taskMode: "auto" | "renewal" | "remarket") {
    if (selected.size === 0) return;
    setBatchBusy(true);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await policiesApi.batchRenewalTasks([...selected], taskMode);
      clearSelection();
      await reload();
      setStatusMessage(`Batch task run complete: ${response.created} created · ${response.skipped} skipped.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch task creation failed");
    } finally {
      setBatchBusy(false);
    }
  }

  async function batchOutreach(assistType: "email" | "call_script") {
    if (selected.size === 0) return;
    const attempted = selected.size;
    setBatchBusy(true);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await policiesApi.batchRenewalOutreach([...selected], assistType);
      const created = response.items.length;
      const skipped = Math.max(0, attempted - created);
      setOutreachModal({
        title: assistType === "email" ? "Batch renewal emails" : "Batch call scripts",
        items: response.items.map((row) => ({
          policy_number: row.policy_number,
          customer_name: row.customer_name,
          content: row.content,
        })),
        hint: response.prompt_hint,
      });
      setStatusMessage(`Batch outreach complete: ${created} created · ${skipped} skipped.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Batch outreach failed");
    } finally {
      setBatchBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Renewals Workbench"
        subtitle="90-day renewal pipeline — remarket triggers, batch tasks, and outreach."
      />
      {error ? <div className="error-banner">{error}</div> : null}
      {statusMessage ? <div className="card renewal-alert">{statusMessage}</div> : null}

      {!data ? (
        <div className="loading-screen" style={{ minHeight: "12rem" }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <section className="stat-grid">
            <StatCard label="Renewals (90d)" value={data.kpis.total_policies} />
            <StatCard label="Remarket flags" value={data.kpis.remarketing_count} />
            <StatCard label="Premium at risk" value={formatCurrency(data.kpis.premium_at_risk_cents)} />
            <StatCard label="Due in 30 days" value={formatCurrency(data.kpis.premium_30_day_cents)} />
          </section>

          {data.kpis.remarketing_count > 0 ? (
            <div className="renewal-alert card">
              <strong>{data.kpis.remarketing_count}</strong> policies flagged for remarketing due to premium
              increases or carrier factors.
            </div>
          ) : null}

          <div className="renewal-toolbar card">
            <div className="renewal-toolbar-left">
              <span className="renewal-selection-count">
                {selected.size} selected
                {data.kpis.scoped_to_user ? " · scoped to your book" : null}
              </span>
              {currentBucket ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => selectBucket(currentBucket)}
                >
                  Select bucket
                </button>
              ) : null}
              <button type="button" className="btn btn-secondary" onClick={clearSelection} disabled={!selected.size}>
                Clear
              </button>
            </div>
            <div className="renewal-toolbar-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selected.size || batchBusy}
                onClick={() => batchCreateTasks("auto")}
              >
                {batchBusy ? "Working…" : "Batch create tasks"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!selected.size || batchBusy}
                onClick={() => batchOutreach("email")}
              >
                Batch draft emails
              </button>
            </div>
          </div>

          {data.total === 0 ? (
            <div className="card empty-state">No renewals due in the next 90 days.</div>
          ) : (
            <div className="card renewal-workbench-panel">
              <div className="renewal-bucket-tabs" role="tablist">
                {data.buckets.map((bucket) => (
                  <button
                    key={bucket.bucket}
                    type="button"
                    role="tab"
                    aria-selected={activeBucket === bucket.bucket}
                    className={`renewal-bucket-tab ${activeBucket === bucket.bucket ? "active" : ""}`}
                    onClick={() => setActiveBucket(bucket.bucket)}
                  >
                    {bucket.label}
                    <span className="renewal-bucket-count">{bucket.count}</span>
                  </button>
                ))}
              </div>
              {currentBucket ? (
                <BucketPanel
                  bucket={currentBucket}
                  selected={selected}
                  onToggle={toggleSelect}
                  busy={busyPolicyId}
                  onTask={createSingleTask}
                />
              ) : null}
            </div>
          )}
        </>
      )}

      {outreachModal ? (
        <div className="modal-backdrop" role="presentation" onClick={() => setOutreachModal(null)}>
          <div className="modal card producer-assist-modal renewal-outreach-modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <h3>{outreachModal.title}</h3>
              <button type="button" className="btn btn-secondary" onClick={() => setOutreachModal(null)}>
                Close
              </button>
            </header>
            {outreachModal.hint ? <p className="producer-assist-hint">{outreachModal.hint}</p> : null}
            <div className="renewal-outreach-list">
              {outreachModal.items.map((item) => (
                <article key={item.policy_number} className="renewal-outreach-item">
                  <strong>
                    {item.policy_number}
                    {item.customer_name ? ` — ${item.customer_name}` : ""}
                  </strong>
                  <pre className="producer-assist-content">{item.content}</pre>
                </article>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
